import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { alpha } from "@mui/material/styles";
import RedeemOutlinedIcon from "@mui/icons-material/RedeemOutlined";
import LocalActivityOutlinedIcon from "@mui/icons-material/LocalActivityOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";

// Every option — "Pay", packages, vouchers — is one choice in the same
// list, so all rows share these. Without the explicit ml/mr, MUI's default -11px
// label margin lands differently on the standalone row than on the RadioGroup
// rows and the radios stop lining up.
const OPTION_LABEL_SX = { ml: 0, mr: 0, alignItems: "center" };
const OPTION_RADIO_SX = { p: 0.75, mr: 0.5 };

// Guests, and clients with no package/voucher/membership to apply, get no panel
// at all. Exported so the summary card that embeds it can tell whether it will
// render anything before reserving a card for it.
export function hasCreditsToApply({ isAuthenticated, packagesLoading, vouchersLoading, packages, vouchers, membershipApplies }) {
  if (!isAuthenticated) return false;
  return Boolean(packagesLoading || vouchersLoading || packages?.length > 0 || vouchers?.length > 0 || membershipApplies);
}

function CheckoutCreditsPanel({
  isAuthenticated,
  packagesLoading,
  vouchersLoading,
  packages,
  vouchers,
  membership,
  membershipApplies,
  membershipSaving,
  isPackageApplied,
  selectedPackageId,
  setSelectedPackageId,
  selectedVoucherId,
  setSelectedVoucherId,
  primaryAfterPackage,
  displayCurrency,
  embedded = false
}) {
  if (!hasCreditsToApply({ isAuthenticated, packagesLoading, vouchersLoading, packages, vouchers, membershipApplies })) {
    return null;
  }

  const body = (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            width: "2.25rem",
            height: "2.25rem",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
            color: "primary.main"
          }}
        >
          <RedeemOutlinedIcon sx={{ fontSize: "1.25rem" }} />
        </Box>
        <Typography sx={{ fontSize: "1.125rem", fontWeight: 600, color: "text.primary" }}>Apply a credit</Typography>
      </Box>

      {membershipApplies && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            border: 1,
            // A saving, so the accent (secondary). The title stays primary: an
            // orange accent is too light for small text on white.
            borderColor: (theme) => alpha(theme.palette.secondary.main, 0.25),
            backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.06),
            display: "flex",
            alignItems: "center",
            gap: 1
          }}
        >
          <WorkspacePremiumOutlinedIcon sx={{ fontSize: "1.125rem", color: "secondary.main" }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} sx={{ color: "primary.main" }}>
              Member pricing applied
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {membership?.name ? `${membership.name} • ` : ""}
              You save {displayCurrency}
              {membershipSaving.toFixed(2)} on this booking
              {isPackageApplied ? " (overridden by package)" : ""}
            </Typography>
          </Box>
        </Box>
      )}

      {packagesLoading || vouchersLoading ? (
        <Box display="flex" alignItems="center" gap={1.5} sx={{ color: "text.secondary" }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Checking your packages and vouchers…</Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          <FormControlLabel
            sx={OPTION_LABEL_SX}
            control={
              <Radio
                size="small"
                sx={OPTION_RADIO_SX}
                checked={!selectedPackageId && !selectedVoucherId}
                onChange={() => {
                  setSelectedPackageId("");
                  setSelectedVoucherId("");
                }}
              />
            }
            label={
              <Typography variant="body2" fontWeight={500}>
                Pay
              </Typography>
            }
          />

          {packages.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mb: 1
                }}
              >
                <LocalActivityOutlinedIcon sx={{ fontSize: "0.875rem" }} />
                Your packages
              </Typography>
              <RadioGroup value={selectedPackageId} onChange={(e) => setSelectedPackageId(e.target.value)}>
                {packages.map((pkg) => (
                  <FormControlLabel
                    key={pkg._id}
                    value={pkg._id}
                    sx={OPTION_LABEL_SX}
                    control={<Radio size="small" sx={OPTION_RADIO_SX} />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {pkg.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {pkg.sessionsRemaining} session{pkg.sessionsRemaining === 1 ? "" : "s"} left
                          {pkg.noExpiry ? " • No expiry" : pkg.expiryDate ? ` • Expires ${new Date(pkg.expiryDate).toLocaleDateString()}` : ""}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </Box>
          )}

          {vouchers.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mb: 1
                }}
              >
                <CardGiftcardOutlinedIcon sx={{ fontSize: "0.875rem" }} />
                Your vouchers
              </Typography>
              <RadioGroup value={selectedVoucherId} onChange={(e) => setSelectedVoucherId(e.target.value)}>
                {vouchers.map((v) => (
                  <FormControlLabel
                    key={v._id}
                    value={v._id}
                    sx={OPTION_LABEL_SX}
                    control={<Radio size="small" sx={OPTION_RADIO_SX} />}
                    disabled={isPackageApplied && primaryAfterPackage === 0}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {v.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Balance {displayCurrency}
                          {(parseFloat(v.remainValue) || 0).toFixed(2)}
                          {v.noExpiry ? " • No expiry" : v.expiryDate ? ` • Expires ${new Date(v.expiryDate).toLocaleDateString()}` : ""}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </Box>
          )}
        </Stack>
      )}
    </>
  );

  // Embedded inside the merged summary card: drop the standalone Paper/border.
  // The summary card draws the dividers between its sections, so it can tell
  // whether anything actually follows this one.
  if (embedded) {
    return <Box sx={{ p: { xs: 2.5, md: 3 } }}>{body}</Box>;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 3 },
        borderRadius: 3,
        border: 1,
        borderColor: (theme) => alpha(theme.palette.divider, 0.6),
        backgroundColor: "background.paper"
      }}
    >
      {body}
    </Paper>
  );
}

export default CheckoutCreditsPanel;
